import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface SiteImage {
  id: string;
  section: string;
  image_key: string;
  image_url: string;
  alt_text: string;
  created_at?: string;
  updated_at?: string;
}

export default function ImagesPage() {
  const navigate = useNavigate();
  const [images, setImages] = useState<SiteImage[]>([]);
  const [editingImage, setEditingImage] = useState<SiteImage | null>(null);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newAltText, setNewAltText] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMethod, setUploadMethod] = useState<'url' | 'file'>('file');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<SiteImage | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/admin/login');
      return;
    }
    loadImages();
  };

  const loadImages = async () => {
    setLoading(true);
    setMessage('');
    
    try {
      // Cargar directamente desde Supabase
      const { data, error } = await supabase
        .from('site_images')
        .select('*')
        .order('section', { ascending: true })
        .order('image_key', { ascending: true });

      if (error) {
        console.error('Error al cargar imágenes:', error);
        throw new Error(error.message);
      }

      if (data && data.length > 0) {
        setImages(data);
      } else {
        // Si no hay imágenes, insertar las iniciales
        await insertInitialImages();
      }
    } catch (error: any) {
      console.error('Error:', error);
      setMessage(`Error al cargar las imágenes: ${error.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const insertInitialImages = async () => {
    try {
      const initialImages = [
        {
          section: 'hero',
          image_key: 'slide_1',
          image_url: 'https://readdy.ai/api/search-image?query=modern%20clean%20water%20purification%20system%20with%20crystal%20clear%20water%20flowing%20through%20advanced%20filtration%20technology%20in%20bright%20minimalist%20setting%20with%20soft%20natural%20lighting%20and%20pure%20white%20background%20professional%20product%20photography&width=1920&height=1080&seq=hero1&orientation=landscape',
          alt_text: 'Sistema de purificación de agua PUREZA - Tecnología avanzada'
        },
        {
          section: 'hero',
          image_key: 'slide_2',
          image_url: 'https://readdy.ai/api/search-image?query=happy%20family%20drinking%20pure%20clean%20water%20in%20modern%20kitchen%20with%20water%20purifier%20bright%20natural%20lighting%20lifestyle%20photography%20with%20simple%20white%20background%20healthy%20living&width=1920&height=1080&seq=hero2&orientation=landscape',
          alt_text: 'Familia disfrutando agua purificada PUREZA'
        },
        {
          section: 'hero',
          image_key: 'slide_3',
          image_url: 'https://readdy.ai/api/search-image?query=close%20up%20of%20crystal%20clear%20water%20droplets%20and%20modern%20water%20filter%20system%20with%20blue%20tones%20and%20white%20background%20macro%20photography%20showing%20purity%20and%20freshness&width=1920&height=1080&seq=hero3&orientation=landscape',
          alt_text: 'Agua cristalina purificada - PUREZA'
        },
        {
          section: 'about',
          image_key: 'main',
          image_url: 'https://readdy.ai/api/search-image?query=professional%20water%20purification%20laboratory%20with%20scientists%20testing%20water%20quality%20modern%20equipment%20and%20technology%20bright%20clean%20environment%20with%20white%20background%20corporate%20photography&width=800&height=600&seq=about1&orientation=landscape',
          alt_text: 'Laboratorio de purificación PUREZA'
        },
        {
          section: 'video',
          image_key: 'background',
          image_url: 'https://readdy.ai/api/search-image?query=abstract%20water%20waves%20and%20ripples%20in%20blue%20and%20white%20tones%20clean%20minimalist%20background%20for%20video%20overlay%20soft%20gradient%20peaceful%20atmosphere&width=1920&height=1080&seq=video1&orientation=landscape',
          alt_text: 'Fondo de video - Agua pura'
        },
        {
          section: 'clients',
          image_key: 'background',
          image_url: 'https://readdy.ai/api/search-image?query=satisfied%20customers%20in%20modern%20office%20environment%20with%20water%20dispenser%20professional%20business%20setting%20bright%20natural%20lighting%20with%20clean%20white%20background%20corporate%20lifestyle&width=1920&height=1080&seq=clients1&orientation=landscape',
          alt_text: 'Clientes satisfechos PUREZA'
        }
      ];

      const { data, error } = await supabase
        .from('site_images')
        .insert(initialImages)
        .select();

      if (error) throw error;

      if (data) {
        setImages(data);
      }
    } catch (error: any) {
      console.error('Error al insertar imágenes iniciales:', error);
      setMessage(`Error al inicializar imágenes: ${error.message}`);
      setMessageType('error');
    }
  };

  const handleEdit = (image: SiteImage) => {
    setEditingImage(image);
    setNewImageUrl(image.image_url);
    setNewAltText(image.alt_text);
    setMessage('');
    setSelectedFile(null);
    setPreviewUrl('');
    setUploadMethod('file');
    setUploadProgress(0);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setMessage('Solo se permiten archivos JPG, PNG, WEBP o GIF');
      setMessageType('error');
      return;
    }

    // Validar tamaño (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage('El archivo no debe superar los 5MB');
      setMessageType('error');
      return;
    }

    setSelectedFile(file);
    
    // Crear vista previa
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
    setMessage('');
  };

  const uploadFileToSupabase = async (file: File): Promise<string | null> => {
    try {
      setUploadProgress(10);
      
      // Generar nombre único para el archivo
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = fileName;

      setUploadProgress(30);

      // Subir archivo a Supabase Storage
      const { data, error } = await supabase.storage
        .from('site-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Error al subir archivo:', error);
        throw error;
      }

      setUploadProgress(70);

      // Obtener URL pública
      const { data: urlData } = supabase.storage
        .from('site-images')
        .getPublicUrl(filePath);

      setUploadProgress(100);

      return urlData.publicUrl;
    } catch (error: any) {
      console.error('Error en uploadFileToSupabase:', error);
      setMessage(`Error al subir archivo: ${error.message}`);
      setMessageType('error');
      return null;
    }
  };

  const handleSave = async () => {
    if (!editingImage) return;
    
    setSaving(true);
    setMessage('');
    setUploadProgress(0);

    try {
      let finalImageUrl = newImageUrl;

      // Si se seleccionó un archivo, subirlo primero
      if (uploadMethod === 'file' && selectedFile) {
        setMessage('Subiendo imagen...');
        setMessageType('success');
        const uploadedUrl = await uploadFileToSupabase(selectedFile);
        
        if (!uploadedUrl) {
          setMessage('Error al subir la imagen. Por favor intenta de nuevo.');
          setMessageType('error');
          setSaving(false);
          return;
        }
        
        finalImageUrl = uploadedUrl;
      }

      // Validar que haya una URL
      if (!finalImageUrl || finalImageUrl.trim() === '') {
        setMessage('Debes proporcionar una URL o seleccionar un archivo');
        setMessageType('error');
        setSaving(false);
        return;
      }

      setMessage('Guardando cambios...');
      setMessageType('success');

      // Actualizar directamente en Supabase
      const { data, error } = await supabase
        .from('site_images')
        .update({ 
          image_url: finalImageUrl, 
          alt_text: newAltText || '',
          updated_at: new Date().toISOString()
        })
        .eq('id', editingImage.id)
        .select()
        .single();

      if (error) {
        console.error('Error al actualizar:', error);
        throw new Error(error.message);
      }

      setMessage('¡Imagen actualizada exitosamente!');
      setMessageType('success');
      
      // Recargar imágenes
      await loadImages();
      
      // Limpiar formulario
      setEditingImage(null);
      setNewImageUrl('');
      setNewAltText('');
      setSelectedFile(null);
      setPreviewUrl('');
      setUploadProgress(0);
      
    } catch (error: any) {
      console.error('Error al guardar:', error);
      setMessage(`Error al guardar los cambios: ${error.message}`);
      setMessageType('error');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (image: SiteImage) => {
    setImageToDelete(image);
    setDeleteModalOpen(true);
    setMessage('');
  };

  const handleDelete = async () => {
    if (!imageToDelete) return;

    setDeleting(true);
    setMessage('');

    try {
      const { error } = await supabase
        .from('site_images')
        .delete()
        .eq('id', imageToDelete.id);

      if (error) {
        throw new Error(error.message);
      }

      setMessage('¡Imagen eliminada exitosamente!');
      setMessageType('success');

      // Recargar imágenes
      await loadImages();

      // Cerrar modal
      setDeleteModalOpen(false);
      setImageToDelete(null);
    } catch (error: any) {
      console.error('Error al eliminar:', error);
      setMessage(`Error al eliminar: ${error.message}`);
      setMessageType('error');
    } finally {
      setDeleting(false);
    }
  };

  const getSectionName = (section: string) => {
    const names: { [key: string]: string } = {
      'hero': 'Slider Principal',
      'about': 'Nosotros',
      'clients': 'Clientes',
      'video': 'Video'
    };
    return names[section] || section;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando imágenes...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout currentPage="images">
      <div className="mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Gestión de Imágenes del Sitio</h2>
        <p className="text-xs sm:text-sm text-gray-600 mt-1">Personaliza las imágenes de tu página web</p>
      </div>

      {/* Hero Section Images */}
      <div className="mb-6 sm:mb-8">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Imágenes del Hero (Carrusel)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {images
            .filter(img => img.section === 'hero')
            .map((image) => (
              <div key={image.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="aspect-video w-full bg-gray-100 relative">
                  <img
                    src={image.image_url}
                    alt={image.alt_text}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <p className="text-xs sm:text-sm text-gray-600 mb-2">{image.alt_text}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(image)}
                      className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap flex items-center justify-center gap-2 text-sm"
                    >
                      <i className="ri-edit-line"></i>
                      Cambiar
                    </button>
                    <button
                      onClick={() => openDeleteModal(image)}
                      className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors whitespace-nowrap flex items-center justify-center gap-2 text-sm"
                      title="Eliminar imagen"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Logo */}
      <div className="mb-6 sm:mb-8">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Logo</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {images
            .filter(img => img.section === 'logo')
            .map((image) => (
              <div key={image.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="aspect-video w-full bg-gray-100 relative flex items-center justify-center p-4">
                  <img
                    src={image.image_url}
                    alt={image.alt_text}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <div className="p-4">
                  <p className="text-xs sm:text-sm text-gray-600 mb-2">{image.alt_text}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(image)}
                      className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap flex items-center justify-center gap-2 text-sm"
                    >
                      <i className="ri-edit-line"></i>
                      Cambiar
                    </button>
                    <button
                      onClick={() => openDeleteModal(image)}
                      className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors whitespace-nowrap flex items-center justify-center gap-2 text-sm"
                      title="Eliminar imagen"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* About Section Images */}
      <div className="mb-6 sm:mb-8">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Sección Nosotros</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {images
            .filter(img => img.section === 'about')
            .map((image) => (
              <div key={image.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="aspect-video w-full bg-gray-100 relative">
                  <img
                    src={image.image_url}
                    alt={image.alt_text}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <p className="text-xs sm:text-sm text-gray-600 mb-2">{image.alt_text}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(image)}
                      className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap flex items-center justify-center gap-2 text-sm"
                    >
                      <i className="ri-edit-line"></i>
                      Cambiar
                    </button>
                    <button
                      onClick={() => openDeleteModal(image)}
                      className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors whitespace-nowrap flex items-center justify-center gap-2 text-sm"
                      title="Eliminar imagen"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Video Section */}
      <div className="mb-6 sm:mb-8">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Sección Video</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {images
            .filter(img => img.section === 'video')
            .map((image) => (
              <div key={image.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="aspect-video w-full bg-gray-100 relative">
                  <img
                    src={image.image_url}
                    alt={image.alt_text}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <p className="text-xs sm:text-sm text-gray-600 mb-2">{image.alt_text}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(image)}
                      className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap flex items-center justify-center gap-2 text-sm"
                    >
                      <i className="ri-edit-line"></i>
                      Cambiar
                    </button>
                    <button
                      onClick={() => openDeleteModal(image)}
                      className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors whitespace-nowrap flex items-center justify-center gap-2 text-sm"
                      title="Eliminar imagen"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Clients Section */}
      <div className="mb-6 sm:mb-8">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Clientes Satisfechos</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {images
            .filter(img => img.section === 'clients')
            .map((image) => (
              <div key={image.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="aspect-video w-full bg-gray-100 relative">
                  <img
                    src={image.image_url}
                    alt={image.alt_text}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <p className="text-xs sm:text-sm text-gray-600 mb-2">{image.alt_text}</p>
                  <button
                    onClick={() => handleEdit(image)}
                    className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap flex items-center justify-center gap-2 text-sm"
                  >
                    <i className="ri-edit-line"></i>
                    Cambiar Imagen
                  </button>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Modal de Edición */}
      {editingImage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-xl font-semibold text-gray-900">
                Editar Imagen - {getSectionName(editingImage.section)}
              </h3>
              <button
                onClick={() => {
                  setEditingImage(null);
                  setNewImageUrl('');
                  setNewAltText('');
                  setSelectedFile(null);
                  setPreviewUrl('');
                  setUploadProgress(0);
                  setMessage('');
                }}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Mensajes */}
              {message && (
                <div
                  className={`p-4 rounded-lg ${
                    messageType === 'success'
                      ? 'bg-green-50 text-green-800 border border-green-200'
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <i
                      className={`${
                        messageType === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'
                      } text-xl`}
                    ></i>
                    <p className="text-sm font-medium">{message}</p>
                  </div>
                </div>
              )}

              {/* Imagen actual */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Imagen Actual</label>
                <div className="aspect-video w-full bg-gray-100 rounded-lg overflow-hidden">
                  <img
                    src={editingImage.image_url}
                    alt={editingImage.alt_text}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Método de carga */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Método de Carga</label>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setUploadMethod('file');
                      setNewImageUrl('');
                    }}
                    className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                      uploadMethod === 'file'
                        ? 'border-teal-600 bg-teal-50 text-teal-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    <i className="ri-upload-2-line text-xl mb-1"></i>
                    <div className="text-sm font-medium">Subir Archivo</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setUploadMethod('url');
                      setSelectedFile(null);
                      setPreviewUrl('');
                    }}
                    className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                      uploadMethod === 'url'
                        ? 'border-teal-600 bg-teal-50 text-teal-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    <i className="ri-link text-xl mb-1"></i>
                    <div className="text-sm font-medium">URL de Imagen</div>
                  </button>
                </div>
              </div>

              {/* Subir archivo */}
              {uploadMethod === 'file' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Seleccionar Nueva Imagen
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-teal-500 transition-colors">
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="file-upload"
                    />
                    <label
                      htmlFor="file-upload"
                      className="cursor-pointer flex flex-col items-center"
                    >
                      <i className="ri-image-add-line text-4xl text-gray-400 mb-2"></i>
                      <p className="text-sm text-gray-600 mb-1">
                        Haz clic para seleccionar una imagen
                      </p>
                      <p className="text-xs text-gray-500">JPG, PNG, WEBP o GIF (máx. 5MB)</p>
                    </label>
                  </div>

                  {/* Vista previa del archivo seleccionado */}
                  {previewUrl && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Vista Previa
                      </label>
                      <div className="aspect-video w-full bg-gray-100 rounded-lg overflow-hidden">
                        <img
                          src={previewUrl}
                          alt="Vista previa"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      {selectedFile && (
                        <p className="text-sm text-gray-600 mt-2">
                          Archivo: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                        </p>
                      )}
                    </div>
                  )}

                  {/* Barra de progreso */}
                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Subiendo...</span>
                        <span className="text-sm font-medium text-teal-600">{uploadProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-teal-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* URL de imagen */}
              {uploadMethod === 'url' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL de la Nueva Imagen
                  </label>
                  <input
                    type="url"
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    placeholder="https://ejemplo.com/imagen.jpg"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
                  />
                  {newImageUrl && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Vista Previa
                      </label>
                      <div className="aspect-video w-full bg-gray-100 rounded-lg overflow-hidden">
                        <img
                          src={newImageUrl}
                          alt="Vista previa"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.src = 'https://via.placeholder.com/800x450?text=Error+al+cargar+imagen';
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Texto alternativo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Texto Alternativo (Alt Text)
                </label>
                <input
                  type="text"
                  value={newAltText}
                  onChange={(e) => setNewAltText(e.target.value)}
                  placeholder="Descripción de la imagen para SEO"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  El texto alternativo mejora el SEO y la accesibilidad
                </p>
              </div>

              {/* Botones */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || (uploadMethod === 'file' && !selectedFile) || (uploadMethod === 'url' && !newImageUrl)}
                  className={`flex-1 px-6 py-3 rounded-lg font-medium transition-colors whitespace-nowrap flex items-center justify-center gap-2 ${
                    saving || (uploadMethod === 'file' && !selectedFile) || (uploadMethod === 'url' && !newImageUrl)
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-teal-600 text-white hover:bg-teal-700'
                  }`}
                >
                  {saving ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Guardando...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line text-lg"></i>
                      Guardar Cambios
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingImage(null);
                    setNewImageUrl('');
                    setNewAltText('');
                    setSelectedFile(null);
                    setPreviewUrl('');
                    setUploadProgress(0);
                    setMessage('');
                  }}
                  disabled={saving}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap font-medium"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación de Eliminación */}
      {deleteModalOpen && imageToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                ¿Eliminar imagen?
              </h3>
              <button
                onClick={() => {
                  setDeleteModalOpen(false);
                  setImageToDelete(null);
                }}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-6 space-y-4">
              {message && (
                <div
                  className={`p-4 rounded-lg ${
                    messageType === 'success'
                      ? 'bg-green-50 text-green-800 border border-green-200'
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <i
                      className={`${
                        messageType === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'
                      } text-xl`}
                    ></i>
                    <p className="text-sm font-medium">{message}</p>
                  </div>
                </div>
              )}

              <div className="text-center">
                <i className="ri-error-warning-line text-red-500 text-5xl mb-3 block"></i>
                <p className="text-gray-700 mb-2">
                  ¿Estás seguro de que quieres eliminar esta imagen?
                </p>
                <p className="text-sm text-gray-500">
                  <strong>{getSectionName(imageToDelete.section)}</strong> - {imageToDelete.alt_text}
                </p>
              </div>

              <div className="aspect-video w-full bg-gray-100 rounded-lg overflow-hidden">
                <img
                  src={imageToDelete.image_url}
                  alt={imageToDelete.alt_text}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className={`flex-1 px-6 py-3 rounded-lg font-medium transition-colors whitespace-nowrap flex items-center justify-center gap-2 ${
                    deleting
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  {deleting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <i className="ri-delete-bin-line text-lg"></i>
                      Sí, eliminar
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setDeleteModalOpen(false);
                    setImageToDelete(null);
                  }}
                  disabled={deleting}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap font-medium"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Instrucciones */}
      <div className="bg-teal-50 border border-teal-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 flex items-center justify-center">
            <i className="ri-information-line text-2xl text-teal-600"></i>
          </div>
          <div>
            <h3 className="font-semibold text-teal-900 mb-2">Instrucciones</h3>
            <ul className="text-sm text-teal-800 space-y-1">
              <li>• Haz clic en "Editar" para cambiar cualquier imagen</li>
              <li>• Puedes subir una imagen desde tu ordenador o pegar una URL</li>
              <li>• Las imágenes se suben automáticamente a un almacenamiento seguro</li>
              <li>• Formatos permitidos: JPG, PNG, WEBP, GIF (máximo 5MB)</li>
              <li>• Actualiza el texto alternativo para mejorar el SEO</li>
              <li>• Los cambios se reflejarán inmediatamente en el sitio web</li>
            </ul>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
